import { Dictionary } from 'lodash';
import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { User } from 'api/models';
import { Button } from 'components/atoms/button';
import { useTranslation } from 'hooks/use-translation';
import { ProjectsWithMembers } from '../types';
import {
  ModalTitle,
  AddToProjectModalFooter as Footer,
  ModalWithStyledFooter,
} from './common';
import { AddUsersModalContent } from './modal-content/user-content';

interface Props {
  projectId?: number;
  projectLookup: Dictionary<ProjectsWithMembers>;
  onUsersAdded: (projectId: number, users: User[]) => void;
  onInviteHandler?: (projectId: number, user: User[]) => Promise<void>;
  onCancel: () => void;
  renderPrimaryButtonContent?: (count: number) => ReactNode;
}
export const AddUsersModal: FC<Props> = ({
  projectId,
  projectLookup,
  onUsersAdded,
  onInviteHandler,
  onCancel,
  renderPrimaryButtonContent,
}) => {
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [actionPending, setActionPending] = useState(false);
  const projectName = useMemo(
    () =>
      projectId != null ? projectLookup[projectId]?.projectName : undefined,
    [projectId, projectLookup],
  );
  const onAdd = useCallback(() => {
    if (projectId == null) {
      return;
    }
    if (onInviteHandler != null) {
      setActionPending(true);
      onInviteHandler(projectId, selectedUsers)
        .then(() => onUsersAdded(projectId, selectedUsers))
        .finally(() => {
          setActionPending(false);
          setSelectedUsers([]);
        });
    } else {
      onUsersAdded(projectId, selectedUsers);
      setSelectedUsers([]);
    }
  }, [onInviteHandler, onUsersAdded, projectId, selectedUsers]);

  return (
    <ModalWithStyledFooter
      visible={projectId != null}
      destroyOnClose
      onCancel={() => {
        setSelectedUsers([]);
        onCancel();
      }}
      title={
        <ModalTitle
          primaryTitle={useTranslation('resource_management.add_users.title')}
          secondaryTitle={projectName}
        />
      }
      footer={
        <Footer>
          <Button
            type="text"
            onClick={() => {
              setSelectedUsers([]);
              onCancel();
            }}
            disabled={actionPending}
          >
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            type="primary"
            disabled={selectedUsers.length === 0}
            onClick={onAdd}
            loading={actionPending}
          >
            {renderPrimaryButtonContent ? (
              renderPrimaryButtonContent(selectedUsers.length)
            ) : (
              <FormattedMessage
                id="resource_management.add_users.add"
                values={{ count: selectedUsers.length }}
              />
            )}
          </Button>
        </Footer>
      }
    >
      {projectId != null ? (
        <AddUsersModalContent
          projectLookup={projectLookup}
          projectId={projectId}
          selectedUsers={selectedUsers}
          updateSelectedUsers={setSelectedUsers}
        />
      ) : null}
    </ModalWithStyledFooter>
  );
};
